import type { BalanceConfig, SkillConfig, WaveConfig } from "@bastion/data";
import type { ResolvedMap } from "../repos/ConfigRepository";
import type { EnemyState, GameState, Random } from "../types";
import { planSpawns, buildSpawningEnemy } from "./spawner";
import { scaleEnemyStats } from "./scaling";

export interface WaveSystemSnapshot {
  waveStartTime: number;
  waveEndTime: number;
  currentWaveConfig: WaveConfig | null;
  waveIndex: number;
  successfullySpawnedCount: number;
}

export interface WaveEnemyCounts {
  total: number;
  successfullySpawned: number;
}

export function scaleWaveGroups(wave: WaveConfig, multiplier: number): WaveConfig {
  if (multiplier === 1) return wave;
  const scaleInt = (count: number): number => Math.max(1, Math.round(count * multiplier));
  return {
    ...wave,
    groups: wave.groups.map((group) => ({ ...group, count: scaleInt(group.count) })),
  };
}

export function countWaveEnemies(wave: WaveConfig): number {
  return wave.groups.reduce((sum, group) => sum + group.count, 0);
}

/**
 * Manages the spawn/progression state machine for a single wave:
 *
 *   idle → spawning → combat (all spawned, none dead) → completed
 *
 * The engine calls `advance` every tick with the current map state. This
 * module is pure — it returns *actions* (spawn, complete, lose) it wants
 * the engine to apply, keeping mutation in one place.
 */
export interface WaveSystemDeps {
  resolvedMap: ResolvedMap;
  random: Random;
  /** Balance 配置（已按难度调整），用于 HP/速度/数量缩放。 */
  balance: BalanceConfig;
  /** 技能配置表，用于初始化怪物周期技能计时器。 */
  skills: Record<string, SkillConfig>;
}

export interface WaveAdvanceResult {
  spawns: Array<Omit<EnemyState, "id">>;
  spawnedIds: string[];
  waveCompleted: boolean;
  playerDamaged: number;
  gameLost: boolean;
  spawnFailedCount: number;
}

export class WaveSystem {
  private waveStartTime = -1;
  private waveEndTime = -1;
  private currentWaveConfig: WaveConfig | null = null;
  private waveIndex = 0;
  private successfullySpawnedCount = 0;

  constructor(private deps: WaveSystemDeps) {}

  /** 恢复已经校验过的活动波次状态。 */
  restore(snapshot: WaveSystemSnapshot): void {
    this.waveStartTime = snapshot.waveStartTime;
    this.waveEndTime = snapshot.waveEndTime;
    this.currentWaveConfig = snapshot.currentWaveConfig
      ? {
          ...snapshot.currentWaveConfig,
          groups: snapshot.currentWaveConfig.groups.map((group) => ({ ...group })),
        }
      : null;
    this.waveIndex = snapshot.waveIndex;
    this.successfullySpawnedCount = snapshot.successfullySpawnedCount;
  }

  snapshot(): WaveSystemSnapshot {
    return {
      waveStartTime: this.waveStartTime,
      waveEndTime: this.waveEndTime,
      currentWaveConfig: this.currentWaveConfig
        ? {
            ...this.currentWaveConfig,
            groups: this.currentWaveConfig.groups.map((group) => ({ ...group })),
          }
        : null,
      waveIndex: this.waveIndex,
      successfullySpawnedCount: this.successfullySpawnedCount,
    };
  }

  /** 使用最新依赖重建（例如切换地图后）。 */
  updateDeps(deps: WaveSystemDeps): void {
    this.deps = deps;
  }

  /** 在当前模拟时间开始生成 `wave`。`waveIndex` 由引擎传入以避免双重数据源。 */
  startWave(wave: WaveConfig, state: GameState, time: number, waveIndex: number): void {
    const countMult = this.deps.balance.enemyCountMultiplier ?? 1.0;
    this.currentWaveConfig = scaleWaveGroups(wave, countMult);
    this.waveIndex = waveIndex;
    this.successfullySpawnedCount = 0;
    this.waveStartTime = time;
    this.waveEndTime = this.lastSpawnTime(this.currentWaveConfig);
    state.waveInProgress = true;
  }

  /**
   * 从 `prevTime` 开始推进波次 `dt` 秒的模拟时间。
   */
  advance(state: GameState, prevTime: number, dt: number): WaveAdvanceResult {
    const result: WaveAdvanceResult = {
      spawns: [],
      spawnedIds: [],
      waveCompleted: false,
      playerDamaged: 0,
      gameLost: false,
      spawnFailedCount: 0,
    };

    if (!state.waveInProgress || !this.currentWaveConfig) return result;

    const resolvedMap = this.deps.resolvedMap;
    const balance = this.deps.balance;
    const plans = planSpawns(this.currentWaveConfig, this.waveStartTime, prevTime, dt);
    const spawnPoint = resolvedMap.map.spawnPoints[0];
    if (!spawnPoint) {
      result.spawnFailedCount += plans.length;
      state.noPathSpawnCount = (state.noPathSpawnCount ?? 0) + plans.length;
      return result;
    }

    for (const plan of plans) {
      const enemyConfig = resolvedMap.enemies[plan.enemyType];
      if (!enemyConfig) continue;
      const scaledStats = scaleEnemyStats(enemyConfig, resolvedMap.map, balance, this.waveIndex);

      const enemy = buildSpawningEnemy(
        plan,
        spawnPoint,
        {
          stats: scaledStats,
          isBoss: enemyConfig.isBoss,
          skills: (enemyConfig.skillIds ?? []).map((skillId) => this.deps.skills[skillId]),
        },
        this.waveIndex,
        state.entityCounter,
        resolvedMap,
        state.towers,
      );
      if (!enemy) {
        // 不存在有效路径——跟踪以便调试，不要静默跳过
        state.noPathSpawnCount = (state.noPathSpawnCount ?? 0) + 1;
        result.spawnFailedCount += 1;
        continue;
      }

      this.successfullySpawnedCount += 1;
      result.spawns.push({
        type: enemy.type,
        hp: enemy.hp,
        maxHp: enemy.maxHp,
        attack: enemy.attack,
        magic: enemy.magic,
        defense: enemy.defense,
        magicResistance: enemy.magicResistance,
        speed: enemy.speed,
        waveIndex: enemy.waveIndex,
        position: enemy.position,
        waypoints: enemy.waypoints,
        currentWaypointIndex: enemy.currentWaypointIndex,
        segmentProgress: enemy.segmentProgress,
        alive: enemy.alive,
        markedDead: enemy.markedDead ?? false,
        statusEffects: enemy.statusEffects,
        skillTimers: enemy.skillTimers,
        regenTimer: enemy.regenTimer,
      });
      state.entityCounter += 1;
    }

    const spawningDone = prevTime + dt >= this.waveEndTime;
    const noLiveEnemies =
      state.enemies.every((enemy) => !enemy.alive || enemy.debugStationary) &&
      result.spawns.length === 0;
    if (spawningDone && noLiveEnemies) {
      result.waveCompleted = true;
      this.currentWaveConfig = null;
      this.waveEndTime = -1;
      state.waveInProgress = false;
    }

    return result;
  }

  waveEnemyCounts(): WaveEnemyCounts {
    return {
      total: this.currentWaveConfig ? countWaveEnemies(this.currentWaveConfig) : 0,
      successfullySpawned: this.successfullySpawnedCount,
    };
  }

  /** 记录活跃波次的最后一次计划生成的时间。 */
  private lastSpawnTime(wave: WaveConfig): number {
    let maxOffset = 0;
    for (const group of wave.groups) {
      const lastSpawnOffset = group.delay + Math.max(0, group.count - 1) * group.spawnInterval;
      if (lastSpawnOffset > maxOffset) maxOffset = lastSpawnOffset;
    }
    return this.waveStartTime + maxOffset;
  }
}
